import { CompartmentView, LinkSavePayload, LinkView, Template } from '@ekkogmbh/apisdk';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { noop } from '../../Common/Helper/PromiseHelper';
import { TemplatePicker } from 'src/TemplateManagement/TemplatePicker';
import { TechnologyPicker } from 'src/Common/Components/Forms/TechnologyPicker';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useTaskCollections from 'src/Common/Providers/TaskCollectionProvider';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import usePagination from 'src/Common/Providers/PaginationProvider';

interface Props {
  link?: LinkView;
  compartment: CompartmentView;
  closeHandler: () => void;
}

export const LinkPanel = ({ link, compartment, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { subscribe } = useTaskCollections();
  const { refresh } = usePagination();
  const { enableSave } = usePanel();

  const [isSaving, startSaving] = useRequest();
  const isEditMode = useMemo(() => link !== undefined, [link]);

  const coordinate = useMemo(() => compartment.coordinate, [compartment.coordinate]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [technology, setTechnology] = useState<string>('');
  const [template, setTemplate] = useState<Template>();
  const [page, setPage] = useState<number>(1);

  const savePayload = useMemo(() => {
    if (template === undefined || technology === '' || deviceId === '') {
      return undefined;
    }

    return {
      coordinate,
      labelId: deviceId,
      technology,
      pageNumber: page,
      templateName: template.name,
    } as LinkSavePayload;
  }, [technology, template, page, deviceId, coordinate]);

  useEffect(() => enableSave(savePayload !== undefined), [savePayload]);

  const saveLink = useCallback(() => {
    if (savePayload === undefined) {
      return;
    }
    startSaving(
      () => api.saveLink(savePayload, subscribe),
      () => {
        enqueueSnackbar('Link saved');
        refresh();
        closeHandler();
      },
    );
  }, [savePayload, closeHandler]);

  const reset = useCallback(() => {
    setDeviceId(link?.labelId ?? '');
    setPage(link?.pageNumber ?? 1);
    setTechnology(link?.technology ?? '');
    setTemplate(link?.template);
  }, [link]);

  useEffect(() => reset(), [link]);

  return (
    <ContentPanel
      isLoading={isSaving}
      steps={[
        {
          title: 'Link',
          contentCallback: () => (
            <CenteredColumn spacing={2} sx={{ width: '50%' }}>
              <StyledFormHeader label={'Device Link'} />
              <StyledTextField
                label={'Coordinate'}
                value={compartment.coordinate}
                disabled={true}
                onChange={noop}
                onPaste={noop}
              />
              <TechnologyPicker
                coordinate={compartment.coordinate}
                selected={technology}
                onChange={(selection) => setTechnology(selection as string)}
                disabled={isEditMode}
              />
              <StyledTextField
                label={'Device ID'}
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                onPaste={(e) => {
                  e.persist();
                  const target = e.target as HTMLInputElement;
                  setDeviceId(target.value);
                }}
              />
              <StyledTextField
                numeric
                type={'number'}
                label={'Page'}
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
                onPaste={(e) => {
                  e.persist();
                  const target = e.target as HTMLInputElement;
                  setPage(Number(target.value));
                }}
                InputProps={{
                  inputProps: {
                    min: 1,
                    max: 9,
                  },
                }}
                inputProps={{ min: 1, max: 9 }}
              />
              <TemplatePicker
                linkableOnly
                coordinate={coordinate}
                selected={template?.name}
                onChangeCallback={(selection) => setTemplate(selection as Template)}
                onErrorCallback={() => {
                  enqueueSnackbar('No linkable templates found.', { variant: 'error' });
                  closeHandler();
                }}
              />
            </CenteredColumn>
          ),
        },
      ]}
      onSave={saveLink}
      onReset={reset}
      onCancel={closeHandler}
    />
  );
};
